import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/reducers';
import { getCurrentCashCycle, saveCashTransaction, getCycleDetails } from '../../../store/actions/financialActions';
import {
    Box,
    Typography,
    Button,
    Paper,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Alert,
    CircularProgress,
    Grid,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Chip
} from '@mui/material';
import MUIDataTable, { MUIDataTableColumnDef } from 'mui-datatables';
import { useSnackbar } from 'notistack';
import { format, parseISO, isValid } from 'date-fns';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const formatBRL = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    try {
        const parsed = parseISO(dateStr);
        return isValid(parsed) ? format(parsed, 'dd/MM/yyyy') : '-';
    } catch {
        return '-';
    }
};

const PAYMENT_TYPES = [
    { value: 1, label: 'Dinheiro' },
    { value: 2, label: 'Cartão Crédito' },
    { value: 3, label: 'Cartão Débito' },
    { value: 4, label: 'Cheque' },
    { value: 5, label: 'PIX' },
];

const IncomeExpense: React.FC = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const currentCashCycle = useSelector((state: RootState) => state.financial.currentCashCycle);
    const cycleDetails = useSelector((state: RootState) => state.financial.cycleDetails);
    const isFetchingDetails = useSelector((state: RootState) => state.financial.isFetchingDetails);
    const isSavingTransaction = useSelector((state: RootState) => state.financial.isSavingTransaction);

    const [transactionType, setTransactionType] = useState<'income' | 'expense'>('income');
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [paymentType, setPaymentType] = useState(1);
    const [person, setPerson] = useState('');

    useEffect(() => {
        // @ts-ignore
        dispatch(getCurrentCashCycle());
    }, [dispatch]);

    useEffect(() => {
        if (currentCashCycle?.id) {
            // @ts-ignore
            dispatch(getCycleDetails(currentCashCycle.id));
        }
    }, [dispatch, currentCashCycle]);

    const handleSubmit = () => {
        if (!currentCashCycle) {
            enqueueSnackbar('É necessário abrir um ciclo de caixa', { variant: 'error' });
            return;
        }
        if (!description.trim()) {
            enqueueSnackbar('Informe a descrição', { variant: 'warning' });
            return;
        }
        const parsedAmount = parseFloat(amount);
        if (isNaN(parsedAmount) || parsedAmount <= 0) {
            enqueueSnackbar('Informe um valor válido', { variant: 'warning' });
            return;
        }

        // @ts-ignore
        dispatch(saveCashTransaction({
            CashCycle_id: currentCashCycle.id,
            TransactionType: transactionType === 'income' ? 1 : 2,
            Amount: parsedAmount,
            Description: description.trim(),
            PaymentType_id: paymentType,
            Person: person.trim() || null,
            TransactionDate: new Date().toLocaleString('sv-SE', { timeZone: 'America/Sao_Paulo' })
        }));

        enqueueSnackbar(
            transactionType === 'income' ? 'Receita registrada com sucesso' : 'Despesa registrada com sucesso',
            { variant: 'success' }
        );

        // Reset form
        setDescription('');
        setAmount('');
        setPerson('');
    };

    // Filter cycle details for display (only non-installment transactions)
    const manualTransactions = cycleDetails.filter((t: any) => !t.Installment_id);

    const columns: MUIDataTableColumnDef[] = [
        {
            name: 'id',
            label: 'ID',
            options: { filter: false, sort: true }
        },
        {
            name: 'TransactionType',
            label: 'Tipo',
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value: number) => (
                    <Chip
                        label={value === 1 ? 'Receita' : 'Despesa'}
                        color={value === 1 ? 'success' : 'error'}
                        size="small"
                        icon={value === 1 ? <AddIcon /> : <RemoveIcon />}
                    />
                )
            }
        },
        {
            name: 'Description',
            label: 'Descrição',
            options: { filter: false, sort: true }
        },
        {
            name: 'Amount',
            label: 'Valor',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: number, tableMeta: any) => {
                    const row = manualTransactions[tableMeta.rowIndex];
                    const isExpense = row?.TransactionType === 2;
                    return (
                        <Typography
                            variant="body2"
                            color={isExpense ? 'error' : 'success.main'}
                            fontWeight="bold"
                        >
                            {isExpense ? '- ' : '+ '}{formatBRL(value || 0)}
                        </Typography>
                    );
                }
            }
        },
        {
            name: 'paymentType',
            label: 'Forma Pgto',
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value: any) => value?.Name || '-'
            }
        },
        {
            name: 'Person',
            label: 'Pessoa',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: string) => value || '-'
            }
        },
        {
            name: 'TransactionDate',
            label: 'Data',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: string) => formatDate(value)
            }
        },
    ];

    const tableOptions = {
        filterType: 'dropdown' as const,
        responsive: 'standard' as const,
        selectableRows: 'none' as const,
        rowsPerPage: 15,
        rowsPerPageOptions: [15, 30, 50],
        download: false,
        print: false,
        textLabels: {
            body: {
                noMatch: 'Nenhuma transação registrada neste ciclo',
                toolTip: 'Ordenar',
            },
            pagination: {
                next: 'Próxima Página',
                previous: 'Página Anterior',
                rowsPerPage: 'Registros por página:',
                displayRows: 'de',
            },
            toolbar: {
                search: 'Pesquisar',
                viewColumns: 'Ver colunas',
                filterTable: 'Filtrar',
            },
            filter: {
                all: 'Todos',
                title: 'Filtros',
                reset: 'Reiniciar',
            },
        },
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">Receitas e Despesas</Typography>
                <Box>
                    {currentCashCycle ? (
                        <Chip label={`Caixa Aberto - Lote #${currentCashCycle.id}`} color="success" />
                    ) : (
                        <Chip label="Caixa Fechado" color="error" />
                    )}
                </Box>
            </Box>

            {!currentCashCycle && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    É necessário abrir um ciclo de caixa para registrar receitas e despesas.
                </Alert>
            )}

            {currentCashCycle && (
                <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>Nova Transação</Typography>

                    <Box sx={{ mb: 2 }}>
                        <ToggleButtonGroup
                            value={transactionType}
                            exclusive
                            onChange={(_e, newVal) => {
                                if (newVal) setTransactionType(newVal);
                            }}
                            size="small"
                        >
                            <ToggleButton value="income" color="success">
                                <AddIcon sx={{ mr: 0.5 }} /> Receita
                            </ToggleButton>
                            <ToggleButton value="expense" color="error">
                                <RemoveIcon sx={{ mr: 0.5 }} /> Despesa
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Descrição"
                                fullWidth
                                variant="outlined"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <TextField
                                label="Valor (R$)"
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <FormControl fullWidth>
                                <InputLabel>Forma de Pagamento</InputLabel>
                                <Select
                                    value={paymentType}
                                    label="Forma de Pagamento"
                                    onChange={(e) => setPaymentType(Number(e.target.value))}
                                >
                                    {PAYMENT_TYPES.map((pt) => (
                                        <MenuItem key={pt.value} value={pt.value}>{pt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <TextField
                                label="Pessoa (opcional)"
                                fullWidth
                                variant="outlined"
                                value={person}
                                onChange={(e) => setPerson(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <Button
                                variant="contained"
                                color={transactionType === 'income' ? 'success' : 'error'}
                                fullWidth
                                sx={{ height: '56px' }}
                                onClick={handleSubmit}
                                disabled={isSavingTransaction}
                            >
                                Registrar
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {isFetchingDetails ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Paper elevation={2}>
                    <MUIDataTable
                        title="Transações do Ciclo Atual"
                        data={manualTransactions}
                        columns={columns}
                        options={tableOptions}
                    />
                </Paper>
            )}
        </Box>
    );
};

export default IncomeExpense;
